import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { UploadTab as FlashCheckUploadTab } from '../../../pages/flashcheck/components/UploadTab';
import { colors, spacing, breakpoints } from '../../../constants/theme';
import {
  TabSection,
  SectionTitle,
  SectionDescription,
  LoadingSpinner
} from '../../../pages/admin/components/styled';
import { 
  getAllCompanies, 
  getCompanyUserCount, 
  FirestoreCompany 
} from '../../../services/creditService';

const UploadDescription = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 12px;
  padding: ${spacing.lg};
  margin-bottom: ${spacing.lg};
  text-align: center;
`;

const DescriptionTitle = styled.h3`
  color: ${colors.text.primary};
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: ${spacing.sm};
`;

const DescriptionText = styled.p`
  color: ${colors.text.secondary};
  line-height: 1.6;
  margin: 0;
`;

const CompanySelectionContainer = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 12px;
  padding: ${spacing.lg};
  margin-bottom: ${spacing.lg};
`;

const CompanySelectionTitle = styled.h3`
  color: ${colors.text.primary};
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: ${spacing.md};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CompanyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
  
  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const CompanyCheckbox = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${spacing.md};
  border: 2px solid ${props => props.$selected ? colors.primary.main : colors.background.tertiary};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$selected ? colors.primary.main + '10' : colors.background.primary};
  
  &:hover {
    border-color: ${colors.primary.main};
    background: ${colors.primary.main}05;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CompanyInfo = styled.div`
  flex: 1;
`;

const CompanyName = styled.div`
  color: ${colors.text.primary};
  font-weight: 600;
  font-size: 0.9rem;
`;

const CompanyDomain = styled.div`
  color: ${colors.text.secondary};
  font-size: 0.8rem;
`;

const SelectedSummary = styled.div`
  background: ${colors.primary.main}10;
  border: 1px solid ${colors.primary.main}30;
  border-radius: 8px;
  padding: ${spacing.md};
  margin-bottom: ${spacing.lg};
`;

const SummaryText = styled.p`
  color: ${colors.text.primary};
  margin: 0;
  font-size: 0.9rem;
`;

const SelectAllButton = styled.button`
  background: transparent;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main};
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 0.85rem;
  margin-bottom: ${spacing.md};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colors.primary.main};
    color: ${colors.background.primary};
  }
`;

interface Company extends FirestoreCompany {
  userCount: number;
}

interface SuperAdminUploadTabProps {
  loading: boolean;
  error: string | null;
  uploadSuccess: boolean;
  setUploadSuccess: (success: boolean) => void;
  setError: (error: string | null) => void;
  loadFiles: (path?: string) => Promise<void>;
  onUploadComplete: () => void;
}

const SuperAdminUploadTab: React.FC<SuperAdminUploadTabProps> = ({
  loading,
  error,
  uploadSuccess,
  setUploadSuccess,
  setError,
  loadFiles,
  onUploadComplete
}) => {
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Load real companies from Firestore
  useEffect(() => {
    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const firestoreCompanies = await getAllCompanies();
        
        // Enrich companies with user count
        const enrichedCompanies = await Promise.all(
          firestoreCompanies.map(async (company) => {
            const userCount = await getCompanyUserCount(company.id);
            return {
              ...company,
              userCount
            };
          })
        );

        setCompanies(enrichedCompanies);
      } catch (error) {
        console.error('Failed to load companies:', error);
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, []);

  const handleCompanyToggle = (companyId: string) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCompanies.length === companies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(companies.map(c => c.id));
    }
  };

  const selectedCompanyNames = companies
    .filter(c => selectedCompanies.includes(c.id))
    .map(c => c.name);

  return (
    <TabSection>
      <SectionTitle>📤 Super Admin FlashCheck Report Upload</SectionTitle>
      <SectionDescription>
        Upload FlashCheck reports and assign them to specific companies. Selected companies will have access 
        to these reports in their FlashCheck Library for analysis and review.
      </SectionDescription>
      
      <CompanySelectionContainer>
        <CompanySelectionTitle>
          🏢 Select Target Companies
        </CompanySelectionTitle>
        
        {loadingCompanies ? (
          <LoadingSpinner>Loading companies...</LoadingSpinner>
        ) : (
          <>
            <SelectAllButton onClick={handleSelectAll} disabled={companies.length === 0}>
              {selectedCompanies.length === companies.length ? 'Deselect All' : 'Select All'}
            </SelectAllButton>
            
            {companies.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                No companies found. Companies will appear here as users sign up.
              </div>
            ) : (
              <CompanyGrid>
                {companies.map((company) => (
                  <CompanyCheckbox
                    key={company.id}
                    $selected={selectedCompanies.includes(company.id)}
                    onClick={() => handleCompanyToggle(company.id)}
                  >
                    <Checkbox
                      type="checkbox"
                      checked={selectedCompanies.includes(company.id)}
                      onChange={() => handleCompanyToggle(company.id)}
                    />
                    <CompanyInfo>
                      <CompanyName>{company.name}</CompanyName>
                      <CompanyDomain>{company.domain} • {company.userCount} users</CompanyDomain>
                    </CompanyInfo>
                  </CompanyCheckbox>
                ))}
              </CompanyGrid>
            )}
          </>
        )}
        
        {selectedCompanies.length > 0 && (
          <SelectedSummary>
            <SummaryText>
              <strong>Selected ({selectedCompanies.length}):</strong> {selectedCompanyNames.join(', ')}
            </SummaryText>
          </SelectedSummary>
        )}
      </CompanySelectionContainer>
      
      <UploadDescription>
        <DescriptionTitle>📋 FlashCheck Report Distribution</DescriptionTitle>
        <DescriptionText>
          Upload FlashCheck reports that will be made available to the selected companies.
          {selectedCompanies.length === 0 
            ? ' Please select at least one company above to proceed with upload.'
            : ` Reports will be accessible to ${selectedCompanies.length} selected ${selectedCompanies.length === 1 ? 'company' : 'companies'}.`
          }
        </DescriptionText>
      </UploadDescription>
      
      {selectedCompanies.length > 0 ? (
        <FlashCheckUploadTab
          loading={loading}
          error={error}
          uploadSuccess={uploadSuccess}
          setUploadSuccess={setUploadSuccess}
          setError={setError}
          loadFiles={loadFiles}
          onUploadComplete={onUploadComplete}
        />
      ) : (
        <UploadDescription>
          <DescriptionTitle>⚠️ Company Selection Required</DescriptionTitle>
          <DescriptionText>
            Please select at least one company above to enable file upload functionality.
            This ensures reports are properly distributed to the intended organizations.
          </DescriptionText>
        </UploadDescription>
      )}
    </TabSection>
  );
};

export default SuperAdminUploadTab;
